/**
 * Bug Mascot Mood / Animation State System
 *
 * Pure logic module — no React, no DOM. Fully unit-testable.
 * Controls which mood the mascot displays and when idle moods rotate.
 */

// ── Mood definitions ─────────────────────────────────────────────────────────

export type MascotMood =
  | 'idle'        // calm breathing, default
  | 'blink'       // quick eye blink
  | 'curious'     // slight tilt, one antenna raise, pupils look around
  | 'sleepy'      // snoring: droopy eyes, open mouth, big "Zzz" float up
  | 'sneeze'      // wind-up, snap forward, particles fly out
  | 'laugh'       // body bounces hard, huge open mouth, eyes squint shut
  | 'cry'         // big visible tears stream down, mouth trembles, body shakes
  | 'hungry'      // eating: food appears, mouth chomps, crumbs fly
  | 'thinking'    // eyes up, antenna curl, "..." thought
  | 'growPop'     // grows large, holds, tiny pop/sparkle, resets
  | 'excited'     // big eyes, antenna bounce (hover state)
  | 'celebrate'   // jump + sparkle (after successful submit)
  | 'worried'     // wide eyes, slight shake (on error)
  | 'running'     // legs pump, body bobs, antenna stream back
  | 'kicking'     // ball appears, leg winds up and kicks, ball flies away

/** Moods the idle system can randomly pick from. Calm + occasional personality. */
export const IDLE_MOODS: MascotMood[] = [
  'blink', 'blink',     // weighted: blink is most common
  'curious',
  'sleepy',
  'sneeze',
  'laugh',
  'hungry',
  'thinking',
  'running',
  'kicking',
]

/** Rare "special" moods that play less frequently. */
export const RARE_MOODS: MascotMood[] = ['cry', 'growPop']

/** Chance (0-1) that a rare mood plays instead of a normal idle mood. */
export const RARE_MOOD_CHANCE = 0.08

/** How long each animation plays before returning to idle (ms). */
export const MOOD_DURATIONS: Record<MascotMood, number> = {
  idle: Infinity,
  blink: 800,
  curious: 2200,
  sleepy: 3500,       // snoring needs time for zzz to float up
  sneeze: 1800,
  laugh: 3200,        // longer laugh, more bounces
  cry: 3200,          // visible tears need time to stream
  hungry: 2800,       // eating animation with chomps
  thinking: 2600,
  growPop: 2400,
  excited: 1400,
  celebrate: 2800,
  worried: 2200,
  running: 2800,      // full run cycle
  kicking: 2600,      // wind-up, kick, ball flies
}

// ── Idle rotation logic ──────────────────────────────────────────────────────

/** Min/max seconds between idle micro-expressions. */
export const IDLE_INTERVAL_MIN_S = 4
export const IDLE_INTERVAL_MAX_S = 8

/**
 * Pick a random delay (ms) for the next idle micro-expression.
 * Accepts an optional random fn for deterministic testing.
 */
export function nextIdleDelay(rand: () => number = Math.random): number {
  const rangeS = IDLE_INTERVAL_MAX_S - IDLE_INTERVAL_MIN_S
  return (IDLE_INTERVAL_MIN_S + rand() * rangeS) * 1000
}

/**
 * Pick a random idle mood, with a small chance of a rare mood.
 * Accepts an optional random fn for deterministic testing.
 */
export function pickIdleMood(rand: () => number = Math.random): MascotMood {
  if (rand() < RARE_MOOD_CHANCE) {
    return RARE_MOODS[Math.floor(rand() * RARE_MOODS.length)]
  }
  return IDLE_MOODS[Math.floor(rand() * IDLE_MOODS.length)]
}

// ── Hover mood ───────────────────────────────────────────────────────────────

/** Returns the mood to show on hover. */
export function hoverMood(): MascotMood {
  return 'excited'
}

// ── Event-driven moods ───────────────────────────────────────────────────────

/** Returns the mood to show after a successful bug submission. */
export function successMood(): MascotMood {
  return 'celebrate'
}

/** Returns the mood to show after a submission error. */
export function errorMood(): MascotMood {
  return 'worried'
}

// ── Reduced motion ───────────────────────────────────────────────────────────

/**
 * Check if the user prefers reduced motion.
 * Returns false during SSR.
 */
export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}